import { Fragment, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fieldWorkApi } from '../api/field-work-api';
import { fieldOpsPeopleApi } from '../api/field-ops-people-api';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import {
  sourceLabels,
  statusLabels,
  type FieldWorkStatus,
} from '../domain/fieldWorkRequest';
import { teamLabels } from '../domain/fieldOpsTeam';
import { formatHours } from '../domain/fieldWorkReporting';

// Semantic tint per lifecycle state -- blocked reads as "waiting" (amber),
// complete as done (green), rejected as dropped (red), the rest neutral blue.
const STATUS_TONE: Record<FieldWorkStatus, string> = {
  NEW: 'var(--muted)',
  TRIAGED: 'var(--bar-fill)',
  IN_PROGRESS: 'var(--bar-fill)',
  BLOCKED: 'var(--warning-text)',
  COMPLETE: 'var(--positive-text)',
  REJECTED: 'var(--danger-text)',
};

/** open / total table shared by the by-team / by-source / by-person panels. */
function LoadTable({
  caption,
  headLabel,
  rows,
}: {
  caption: string;
  headLabel: string;
  rows: { key: string; label: string; open: number; total: number; to?: string }[];
}) {
  const maxTotal = Math.max(1, ...rows.map((row) => row.total));
  return (
    <section className="panel" aria-label={caption}>
      <h2>{caption}</h2>
      <div className="table-wrap reference-table">
        <table className="load-table">
          <thead>
            <tr>
              <th scope="col">{headLabel}</th>
              <th scope="col" className="num">
                Open
              </th>
              <th scope="col" className="num">
                Total
              </th>
              <th scope="col" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th scope="row">
                  {row.to ? <Link to={row.to}>{row.label}</Link> : row.label}
                </th>
                <td className="num">{row.open.toLocaleString()}</td>
                <td className="num">{row.total.toLocaleString()}</td>
                <td className="load-bar-cell">
                  <span className="load-bar" aria-hidden="true">
                    <span
                      className="load-bar-total"
                      style={{ width: `${(row.total / maxTotal) * 100}%` }}
                    >
                      <span
                        className="load-bar-open"
                        style={{ width: `${row.total ? (row.open / row.total) * 100 : 0}%` }}
                      />
                    </span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StatPanel({
  caption,
  rows,
}: {
  caption: string;
  rows: { label: string; value: string }[];
}) {
  return (
    <section className="panel" aria-label={caption}>
      <h2>{caption}</h2>
      <dl className="report-stats">
        {rows.map((row) => (
          <Fragment key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </Fragment>
        ))}
      </dl>
    </section>
  );
}

export function FieldWorkReportsPage() {
  const statsQuery = useQuery({
    queryKey: ['field-work', 'stats'],
    queryFn: () => fieldWorkApi.getFieldWorkStats(),
  });
  const people = useQuery({
    queryKey: ['field-ops-people'],
    queryFn: () => fieldOpsPeopleApi.listFieldOpsPeople(),
    staleTime: 30 * 60 * 1000,
  });

  const nameByEmail = useMemo(() => {
    const map = new Map<string, string>();
    for (const person of people.data ?? []) map.set(person.email.toLowerCase(), person.displayName);
    return map;
  }, [people.data]);

  const stats = statsQuery.data ?? null;

  if (statsQuery.isLoading) return <PageLoadingState label="Loading field work…" />;
  if (statsQuery.isError || !stats) {
    return (
      <ErrorState
        title="Field work reports could not be loaded"
        rpcName="GetFieldWorkStats"
        onRetry={() => void statsQuery.refetch()}
      />
    );
  }

  const headline: { label: string; value: number; to: string; tone?: string | undefined }[] = [
    { label: 'Open', value: stats.open, to: '/field-work' },
    {
      label: 'Overdue',
      value: stats.overdue,
      to: '/field-work?sort=due&dir=asc',
      tone: stats.overdue > 0 ? 'danger' : undefined,
    },
    {
      label: 'Unassigned (open)',
      value: stats.unassignedOpen,
      to: '/field-work?assignee=unassigned',
      tone: stats.unassignedOpen > 0 ? 'warning' : undefined,
    },
    {
      label: 'Blocked now',
      value: stats.blocked.currentlyBlocked,
      to: '/field-work?status=BLOCKED',
      tone: stats.blocked.currentlyBlocked > 0 ? 'warning' : undefined,
    },
    {
      label: 'Completed',
      value: stats.byStatus.COMPLETE,
      to: '/field-work?status=COMPLETE',
      tone: 'positive',
    },
    { label: 'Total', value: stats.total, to: '/field-work' },
  ];

  const maxStatus = Math.max(1, ...Object.values(stats.byStatus));

  return (
    <>
      <PageHeader
        backLink={<Link to="/field-work">Field work</Link>}
        title="Field work reports"
        description="Backlog, workload, completion time and time blocked across the queue. Every figure links to the matching queue view."
      />

      <section className="metric-row" aria-label="Headline numbers">
        {headline.map((tile) => (
          <Link
            className={`metric-tile${tile.tone ? ` tone-${tile.tone}` : ''}`}
            key={tile.label}
            to={tile.to}
          >
            <span className="metric-label">{tile.label}</span>
            <strong className="metric-value">{tile.value.toLocaleString()}</strong>
          </Link>
        ))}
      </section>

      <section className="panel" aria-label="Backlog by status">
        <h2>Backlog by status</h2>
        <div className="status-bars">
          {Object.entries(stats.byStatus).map(([status, count]) => (
            <Link className="status-bar" key={status} to={`/field-work?status=${status}`}>
              <span className="status-bar-label">
                {statusLabels[status as keyof typeof statusLabels]}
              </span>
              <span className="status-bar-track" aria-hidden="true">
                <span
                  className="status-bar-fill"
                  style={{
                    width: `${(count / maxStatus) * 100}%`,
                    background: STATUS_TONE[status as FieldWorkStatus],
                  }}
                />
              </span>
              <span className="status-bar-value mono">{count}</span>
            </Link>
          ))}
        </div>
      </section>

      <LoadTable
        caption="By team"
        headLabel="Team"
        rows={Object.entries(stats.byTeam).map(([team, load]) => ({
          key: team,
          label: teamLabels[team as keyof typeof teamLabels],
          open: load.open,
          total: load.total,
          to: `/field-work?team=${team}`,
        }))}
      />

      <LoadTable
        caption="By request source"
        headLabel="Source"
        rows={stats.bySource.map((row) => ({
          key: row.source,
          label: row.source === 'UNSPECIFIED' ? 'Unspecified' : sourceLabels[row.source],
          open: row.open,
          total: row.total,
          ...(row.source === 'UNSPECIFIED' ? {} : { to: `/field-work?source=${row.source}` }),
        }))}
      />

      <LoadTable
        caption="By assignee"
        headLabel="Assignee"
        rows={stats.byAssignee.map((row) => ({
          key: row.assignee || 'unassigned',
          label: row.assignee
            ? (nameByEmail.get(row.assignee.toLowerCase()) ?? row.assignee)
            : 'Unassigned',
          open: row.open,
          total: row.total,
          to: `/field-work?assignee=${encodeURIComponent(row.assignee || 'unassigned')}`,
        }))}
      />

      <div className="report-duo">
        <StatPanel
          caption="Completion time"
          rows={[
            { label: 'Completed requests', value: String(stats.completion.count) },
            {
              label: 'Lead time · raised → done',
              value: `${formatHours(stats.completion.leadHoursAvg)} avg · ${formatHours(
                stats.completion.leadHoursMedian,
              )} median`,
            },
            {
              label: 'Cycle time · started → done',
              value:
                stats.completion.cycleCount > 0
                  ? `${formatHours(stats.completion.cycleHoursAvg)} avg · ${formatHours(
                      stats.completion.cycleHoursMedian,
                    )} median`
                  : '—',
            },
          ]}
        />
        <StatPanel
          caption="Time blocked"
          rows={[
            { label: 'Requests ever blocked', value: String(stats.blocked.count) },
            { label: 'Blocked right now', value: String(stats.blocked.currentlyBlocked) },
            { label: 'Total time blocked', value: formatHours(stats.blocked.totalHours) },
            {
              label: 'Average per blocked request',
              value: formatHours(stats.blocked.avgHoursPerRequest),
            },
          ]}
        />
      </div>

      <p className="drawer-version-note">
        Aggregated by <code>GetFieldWorkStats</code>, with a browser-side fallback if the backend
        doesn’t have that RPC yet.
      </p>
    </>
  );
}
